import type { ContactMethod } from '@integration-layer/ampliance/schemas/contact-method-schema.localized'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'

/**
 * Manages the Contact Us side slide functionality.
 *
 * @function useContactUsSideSlide
 * @returns {Object} An object with contact methods and interaction handlers.
 *
 * @description
 * This composable:
 * - Fetches contact information and methods from CMS
 * - Provides methods for handling contact interactions
 * - Supports different contact methods and tracking
 *
 * @example
 * const { contactUsDescription, contactMethods, handleContactClick } = useContactUsSideSlide()
 */
export const useContactUsSideSlide = () => {
  const router = useRouter()
  const localePath = useLocalePath()
  const { checkoutUrl } = useRouteHelper()
  const { openLink, isChatMethod } = useContacts()
  const { openInsideChat } = useInsideChat()
  const { dispatchActionContactUsEvent, dispatchActionContactCheckoutEvent } =
    useGAActionEvents()
  const { closeDialog } = useDialog()

  /**
   * Asynchronously fetches contact information and methods.
   *
   * @type {AsyncData}
   * @property {string} description - Description of contact us section
   * @property {ContactMethod[]} contacts - Available contact methods
   */
  const { data } = useAsyncData(
    'CONTACT_US_CONTENT',
    async () => {
      const query = {
        locale: localePath(''),
        vse: useRoute().query.vse,
        contentId: useRoute().query.contentId,
      }

      const [contactUsInfos, contacts] = await Promise.allSettled([
        $fetch('/api/cms/getInfosWithTitle', {
          query: {
            ...query,
            deliveryKey: 'contact-us-info',
          },
        }),
        $fetch('/api/cms/getAllContacts', {
          query: {
            ...query,
            deliveryKey: 'all-contact-methods',
          },
        }),
      ])

      return {
        description: isPromiseFullfilled(contactUsInfos)?.content?.label ?? '',
        contacts: isPromiseFullfilled(contacts)?.content.methods ?? [],
      }
    },
    {
      server: false,
      default: () => ({ description: '', contacts: [] }),
      getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
    }
  )

  /**
   * Computed description for the contact us section.
   *
   * @type {ComputedRef<string>}
   */
  const contactUsDescription = computed(() => data.value.description)

  /**
   * Filtered contact methods based on country and visibility settings.
   *
   * @type {ComputedRef<ContactMethod[]>}
   */
  const contactMethods = computed(
    () =>
      data.value.contacts.filter(
        contact =>
          !contact.hideInAllCountries?.value?.sidebar &&
          !contact.hideInCountry?.sidebar
      ) ?? []
  )

  /**
   * Determines if the current page is a checkout page.
   *
   * @type {ComputedRef<boolean>}
   */
  const isCheckoutPage = computed(() =>
    router.currentRoute.value.path.includes(checkoutUrl)
  )

  /**
   * Dispatches click events for contact methods based on page context.
   *
   * @param {string} title - Title or name of the contact method
   */
  const dispatchClickEvent = (title: string) => {
    if (isCheckoutPage.value) {
      dispatchActionContactCheckoutEvent('popup', title)
      return
    }
    dispatchActionContactUsEvent()
  }

  /**
   * Handles contact method click interactions.
   *
   * @param {string} name - Name of the contact method
   * @param {ContactMethod['cta']} cta - Call-to-action details for the contact method
   */
  const handleContactClick = (name: string, cta: ContactMethod['cta']) => {
    dispatchClickEvent(cta?.externalLink?.link ?? name)

    if (isChatMethod(name)) {
      closeDialog(SIDEBAR_IDS.cartContactUsSideSlide)
      openInsideChat()
      return
    }

    const _link = cta?.internalCta?.link
    if (!_link) return
    openLink(_link, cta?.internalCta?.type)
  }

  return {
    contactUsDescription,
    contactMethods,
    handleContactClick,
  }
}
